import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { 
  LogIn, 
  Truck, 
  MapPin, 
  FileText, 
  BarChart3, 
  Shield,
  Clock,
  Users,
  Zap,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import cashxService from '@/services/cashxService';

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    {
      icon: <MapPin className="h-12 w-12 text-blue-400" />,
      title: "GPS Echtzeit-Tracking",
      description: "Verfolgen Sie Ihre gesamte Flotte in Echtzeit auf einer interaktiven Karte"
    },
    {
      icon: <FileText className="h-12 w-12 text-green-400" />,
      title: "Automatische Belege",
      description: "Tankbelege und Einkäufe direkt zu Lexware exportieren - kein manueller Aufwand"
    },
    {
      icon: <Shield className="h-12 w-12 text-orange-400" />,
      title: "Schadensmanagementsystem",
      description: "Dokumentieren Sie Fahrzeugschäden und klären Sie Verantwortlichkeiten"
    },
    {
      icon: <Clock className="h-12 w-12 text-purple-400" />,
      title: "Zeiterfassung & Planung",
      description: "Mitarbeiter-Zeiterfassung, Schichtplanung und automatische Urlaubsverwaltung"
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-cyan-400" />,
      title: "Lexware Integration",
      description: "Nahtlose Synchronisation mit Ihrem Lexware-Buchhaltungssystem"
    },
    {
      icon: <Users className="h-12 w-12 text-pink-400" />,
      title: "Team-Kommunikation",
      description: "Integrierter Chat mit Walkie-Talkie Funktion für schnelle Absprachen"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [features.length]);

  /**
   * Unified Login Handler
   * Primary: Cash-X Backend for Drivers
   * Fallback: Flotto Backend for Admins
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('auth.fillEmailPassword'));
      return;
    }

    setLoading(true);
    
    try {
      // First, try Cash-X Login (for drivers)
      const cashxResult = await cashxService.login(email, password);
      
      if (cashxResult.success) {
        // Sync restaurant data (branding, location, settings)
        const syncResult = await cashxService.syncData();
        
        if (syncResult.success) {
          const restaurantName = syncResult.data.restaurant?.name || 'Restaurant';
          toast.success(`Willkommen bei ${restaurantName}!`, {
            description: `Angemeldet als ${cashxResult.driver.name}`
          });
        } else {
          toast.success(`Willkommen, ${cashxResult.driver.name}!`, {
            description: 'Anmeldung erfolgreich'
          });
        }
        
        // Navigate to driver dashboard
        navigate('/dashboard', { 
          state: { 
            user: {
              id: cashxResult.driver.id,
              name: cashxResult.driver.name,
              email: cashxResult.driver.email,
              role: 'driver',
              tenant_id: cashxResult.driver.tenant_id
            }
          }
        });
        return;
      }
      
      // If Cash-X fails, try Flotto Backend (for admins)
      try {
        const response = await axios.post('/auth/login', {
          email,
          password
        });

        if (response.data.success) {
          // ✅ CRITICAL: Save token to localStorage
          localStorage.setItem('token', response.data.token);
          
          toast.success(t('auth.loginSuccess'));
          
          // Redirect based on user role
          let targetPath = '/dashboard';
          if (response.data.user.role === 'super_admin') {
            targetPath = '/super-admin/overview';
          } else if (response.data.user.role === 'admin') {
            targetPath = '/admin';
          }
          
          navigate(targetPath, { state: { user: response.data.user } });
          return;
        }
      } catch (flottoError) {
        // Both logins failed
        console.error('Flotto Login Error:', flottoError);
      }
      
      // Show error from Cash-X
      toast.error('Anmeldung fehlgeschlagen', {
        description: cashxResult.error || 'Ungültige E-Mail oder Passwort'
      });
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error(t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    // Use admin credentials for demo (admins still use Flotto backend)
    setEmail('admin@zozo-burger.de');
    setPassword('admin123');
    toast.info('Admin-Demo Zugangsdaten eingefügt', {
      description: 'Klicken Sie auf Anmelden'
    });
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-gray-900 via-[#0a0a0a] to-gray-900">
      {/* Left Side - Feature Showcase */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
        <div className="absolute inset-0 backdrop-blur-3xl"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="relative z-10 flex flex-col justify-center px-16 w-full">
          {/* Logo and Brand */}
          <div className="mb-12 animate-fade-in">
            <div className="flex items-center gap-4 mb-4">
              <img 
                src="/flotto-logo.png"
                alt="Flotto"
                className="h-20 w-20 object-contain"
              />
              <div>
                <h1 className="text-5xl font-bold text-white">flotto.app</h1>
                <p className="text-sm text-blue-400 mt-1">Fleet Management SaaS</p>
              </div>
            </div>
            <p className="text-xl text-gray-300">
              Die moderne Fuhrparkverwaltung für erfolgreiche Unternehmen
            </p>
          </div>

          {/* Animated Feature Cards */}
          <div className="space-y-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`transition-all duration-700 transform ${
                  currentFeature === index
                    ? 'opacity-100 translate-x-0 scale-100'
                    : 'opacity-0 -translate-x-10 scale-95 absolute'
                }`}
              >
                <Card className="bg-white/5 border-white/10 backdrop-blur-xl hover:bg-white/10 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 p-3 bg-gradient-to-br from-white/10 to-white/5 rounded-xl">
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-300 text-lg leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          {/* Feature Indicators */}
          <div className="flex gap-2 mt-8">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentFeature(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  currentFeature === index
                    ? 'w-12 bg-blue-500'
                    : 'w-6 bg-gray-600 hover:bg-gray-500'
                }`}
              />
            ))}
          </div>

          {/* Key Benefits */}
          <div className="mt-12 grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm">14 Tage kostenlos</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm">Keine Kreditkarte</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm">24/7 Support</span>
            </div>
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm">DSGVO-konform</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <Truck className="h-7 w-7 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white">flotto.app</h1>
            </div>
            <p className="text-gray-400">Fuhrparkverwaltung</p>
          </div>

          <Card className="bg-[#111]/80 border-gray-800 backdrop-blur-xl shadow-2xl">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-white mb-2">Willkommen zurück</h2>
                <p className="text-gray-400">Melden Sie sich an, um fortzufahren</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-gray-300 text-sm font-medium">
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ihre.email@unternehmen.de"
                    className="mt-1.5 bg-[#1a1a1a] border-gray-700 text-white h-12 focus:border-blue-500 transition-colors"
                    data-testid="email-input"
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="text-gray-300 text-sm font-medium">
                    {t('auth.password')}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1.5 bg-[#1a1a1a] border-gray-700 text-white h-12 focus:border-blue-500 transition-colors"
                    data-testid="password-input"
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-700 bg-[#1a1a1a] text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                    />
                    <span className="text-sm text-gray-400">{t('auth.rememberMe')}</span>
                  </label>
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base shadow-lg shadow-blue-600/30 transition-all hover:shadow-blue-600/50 hover:scale-[1.02]"
                  data-testid="login-button"
                >
                  {loading ? (
                    <>
                      <Zap className="mr-2 h-5 w-5 animate-spin" />
                      {t('auth.login')}...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-5 w-5" />
                      {t('auth.login')}
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-800"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#111] text-gray-500">oder</span>
                </div>
              </div>

              {/* Demo Access */}
              <Button
                type="button"
                onClick={handleDemoLogin}
                variant="outline"
                className="w-full h-12 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white transition-all"
              >
                <Zap className="mr-2 h-5 w-5 text-yellow-400" />
                Admin-Demo testen
              </Button>

              {/* Driver Info */}
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Truck className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-300">
                    <strong>Fahrer-Anmeldung</strong>
                    <p className="text-green-400/80 mt-1">
                      Zugangsdaten erhalten Sie von Ihrem Restaurant-Admin.
                    </p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-300">
                    <strong>Sicher & DSGVO-konform</strong>
                    <p className="text-blue-400/80 mt-1">
                      Ihre Daten werden verschlüsselt übertragen und in Deutschland gehostet.
                    </p>
                  </div>
                </div>
              </div>

              {/* Help Text */}
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  {t('auth.noAccount')}{' '}
                  <a href="/register" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
                    {t('auth.register')}
                    <ArrowRight className="inline h-4 w-4 ml-1" />
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>
              © 2025 flotto.app - Alle Rechte vorbehalten
            </p>
            <div className="flex items-center justify-center gap-4 mt-2">
              <a href="/datenschutz" className="hover:text-gray-400 transition-colors">
                Datenschutz
              </a>
              <span>•</span>
              <a href="/impressum" className="hover:text-gray-400 transition-colors">
                Impressum
              </a>
              <span>•</span>
              <a href="/agb" className="hover:text-gray-400 transition-colors">
                AGB
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
